import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type {
  BillingProfileRow,
  CatalogueProtocol, CursorPage, InvoicePage, InvoiceRow, MarginReport, PaymentSubmissionQueueRow,
  RateCardRow, SettlementRow,
} from '@dru/types';
import { EXAM_TYPE_LABEL, INVOICE_STATUS_LABEL, SETTLEMENT_STATUS_LABEL, TAX_TREATMENT_LABEL, formatPaise } from '@dru/types';
import {
  ActionNotice, Alert, Button, Checkbox, ConfirmDialog, DataTable, EmptyState, ErrorState, Field, IconAction, InfoTip, Input, KeyFigures,
  RowActions, SearchSelect, Select, StatusBadge, Tabs, type Tone,
} from '@dru/ui';
import { BadgeCheck, Check, CheckCircle2, Send, X } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { formatDate } from '../../../lib/format';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { Dialog } from '../../../components/Dialog';
import { BillablePlantSelect } from '../../../components/BillablePlantSelect';
import { STANDING_TONE, invoiceName, isDraftNumber } from '../../../components/InvoiceDetailPage';
import { CompanySelect } from '../components/CompanySelect';
import { useCompanyChoice } from '../lib/companyChoice';
import { useFinanceCompanies } from '../lib/useFinanceCompanies';
import { localToday } from '../../../components/PaymentSubmissions';
import { STANDING_LABEL, periodLabel, standingOf } from '../../hr/lib/billing-view';
import { BillingProfiles } from '../components/BillingProfiles';

/**
 * What companies are charged and pay, and what the platform owes its venues
 * and physicians.
 *
 * Six jobs share this address, and each tab is one of them — stated in a
 * sentence at the top of the tab, with only the figures and actions that job
 * needs. The receivable tabs are "for a company": the picker names the
 * subject of every request, and each write is bound and audited against it.
 * The payable tabs are not — a lab serves several companies and is paid once
 * for the month — so they show no picker at all rather than a disabled one.
 *
 * The two halves of the receivable side are deliberately unequal in ceremony.
 * A rate card can be drafted and re-drafted freely and becomes permanent the
 * moment it is published. An invoice can be generated as often as you like,
 * because the database refuses to bill the same examination twice, and then
 * becomes a statement to a customer the moment it is issued. Both permanent
 * acts are confirmed with what they do and that they cannot be undone.
 */
type BillingTab = 'invoices' | 'cards' | 'quotes' | 'profiles' | 'labs' | 'physicians' | 'margin';

const TABS: Record<BillingTab, { label: string; job: string; perCompany: boolean; need: string }> = {
  invoices: {
    label: 'Company invoices', perCompany: true, need: 'invoice.read',
    job: 'Bill a plant for its certified examinations, issue the invoice, and see what the company has paid and still owes.',
  },
  cards: {
    label: 'Rate cards', perCompany: true, need: 'ratecard.read',
    job: 'The prices a company has agreed to. Invoices are priced on the card in force on the examination date; a draft can be changed, a published card is permanent.',
  },
  quotes: {
    label: 'Quotes', perCompany: true, need: 'quote.read',
    job: 'Price proposals to a company: approve discounts, send the quote, and turn an accepted quote into its rate card.',
  },
  profiles: {
    label: 'Billing profiles', perCompany: false, need: 'settlement.read',
    job: 'Sets of tests priced as one item (CBC, liver profile). Lab agreements and customer price lists price them; report sections never do.',
  },
  labs: {
    label: 'Lab payables', perCompany: false, need: 'settlement.read',
    job: 'What the platform owes each venue for completed work, across every company.',
  },
  physicians: {
    label: 'Physician payables', perCompany: false, need: 'settlement.read',
    job: 'What the platform owes each certifying physician for signed reviews, across every company.',
  },
  margin: {
    label: 'Margin', perCompany: true, need: 'settlement.read',
    job: 'What a company was invoiced against what was paid out to venues and physicians for the same examinations.',
  },
};
const ORDER: BillingTab[] = ['invoices', 'cards', 'quotes', 'profiles', 'labs', 'physicians', 'margin'];

/**
 * A date as the browser's calendar reads it. `toISOString()` is the UTC date:
 * in India the first of the month at midnight is the 31st in UTC, which is
 * how invoices came to be generated for "31 Aug – 29 Sep".
 */
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function Billing() {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const visible = ORDER.filter((t) => permissions.has(TABS[t].need));
  const asked = params.get('tab') as BillingTab | null;
  const tab: BillingTab = asked && visible.includes(asked) ? asked : (visible[0] ?? 'invoices');
  const meta = TABS[tab];

  const companies = useFinanceCompanies(params.get('company'));
  const list = companies.data?.items ?? [];
  // The link's company, else the one last chosen in this browser, else — with
  // more than one to pick from — nobody: the screen asks rather than showing
  // ₹0 for whichever company happened to come first (lib/companyChoice).
  const chosen = useCompanyChoice(list, params.get('company')).id;

  // Submissions waiting for Finance. Read once here: the notice above the
  // tabs and the invoice tab's "awaiting verification" both come from it.
  const canConfirm = permissions.has('invoice.payment-confirm');
  const pending = useQuery<{ items: PaymentSubmissionQueueRow[] }>(
    () => (canConfirm ? api.get('/payment-submissions?status=PENDING&limit=200') : Promise.resolve({ items: [] })), [canConfirm],
  );
  const totals = useQuery<{ count: number; amountPaise: number; companyCount: number }>(
    () => canConfirm ? api.get('/payment-submissions/summary?status=PENDING') : Promise.resolve({ count: 0, amountPaise: 0, companyCount: 0 }), [canConfirm]);
  const pendingRows = pending.data?.items ?? [];
  // Companies with submissions waiting, for the "choose a company" state.
  const waiting = [...pendingRows.reduce((m, r) => {
    if (!list.some((c) => c.id === r.tenantId)) return m;
    const w = m.get(r.tenantId) ?? { id: r.tenantId, name: r.tenant.name, count: 0 };
    w.count += 1;
    return m.set(r.tenantId, w);
  }, new Map<string, { id: string; name: string; count: number }>()).values()].slice(0, 4);

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) next.set(k, v);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Billing"
        hint={(
          <>
            Money in from companies, and money out to venues and physicians. Each tab is one job.
            {visible.length > 0 && <span className="mt-1.5 block"><strong className="font-semibold">{meta.label}:</strong> {meta.job}</span>}
          </>
        )}
      />

      {canConfirm && totals.error && <ErrorState detail="Payment totals could not be loaded." onRetry={totals.reload} />}
      {(totals.data?.count ?? 0) > 0 && !totals.error && (
        <ActionNotice
          tone="pending"
          onClick={() => navigate('/admin/payments')}
          hint={`${formatPaise(totals.data?.amountPaise ?? 0)} submitted by ${(totals.data?.companyCount ?? 0)} compan${(totals.data?.companyCount ?? 0) === 1 ? 'y' : 'ies'} — not counted as paid until you confirm it. Open Payment review.`}
        >
          {totals.data?.count} payment submission{totals.data?.count === 1 ? '' : 's'} waiting for verification
        </ActionNotice>
      )}

      {visible.length === 0 ? (
        <EmptyState title="Billing is not part of your access" hint="Ask a platform administrator if you need to read invoices or payables." />
      ) : (
        <>
          <Tabs
            tabs={visible.map((t) => ({ key: t, label: TABS[t].label }))}
            active={tab}
            onChange={(k) => set({ tab: k })}
          />

          {/* The tab's job is in the page's ⓘ, so this row is only the picker.
              On invoices it sits in the list's own toolbar instead, beside
              "Show" (owner, 30 Oct 2026), once a company is chosen. */}
          {meta.perCompany && list.length > 0 && (tab !== 'invoices' || !chosen) && (
            <div className="flex flex-wrap items-end gap-3">
              <CompanySelect
                className="w-full sm:w-80" list={list} value={chosen} truncated={!!companies.data?.nextCursor}
                onChange={(id) => set({ company: id })}
              />
            </div>
          )}

          {tab === 'profiles' ? (
            <BillingProfiles />
          ) : tab === 'labs' ? (
            <Payables kind="PARTNER" />
          ) : tab === 'physicians' ? (
            <Payables kind="PHYSICIAN" />
          ) : companies.error ? (
            <ErrorState detail={companies.error} onRetry={companies.reload} />
          ) : companies.loading ? (
            <p className="text-small text-neutral-500">Loading companies…</p>
          ) : list.length === 0 ? (
            <EmptyState
              title="No active company to bill yet"
              hint="Billing starts once a company is approved and active. Approve a registration from Companies."
            />
          ) : !chosen ? (
            <EmptyState
              title="Choose a company"
              hint={`${meta.label} are kept per company. Pick one above — it is remembered on this browser for next time.`}
              // The companies with something waiting are the likeliest reason
              // Finance is here; one click rather than a search of the picker.
              action={waiting.length > 0 ? (
                <div className="flex flex-wrap justify-center gap-2">
                  {waiting.map((w) => (
                    <Button key={w.id} variant="secondary" size="sm" onClick={() => set({ company: w.id })}>
                      {w.name} · {w.count} payment{w.count === 1 ? '' : 's'} to verify
                    </Button>
                  ))}
                </div>
              ) : undefined}
            />
          ) : tab === 'invoices' ? (
            <Invoices
              key={chosen} tenantId={chosen} pending={canConfirm ? pendingRows.filter((r) => r.tenantId === chosen) : null}
              picker={(
                <CompanySelect
                  className="w-full sm:w-80" list={list} value={chosen} truncated={!!companies.data?.nextCursor}
                  onChange={(id) => set({ company: id })}
                />
              )}
            />
          ) : tab === 'cards' ? (
            <RateCards key={chosen} tenantId={chosen} company={list.find((c) => c.id === chosen)?.name ?? 'this company'} />
          ) : tab === 'quotes' ? (
            <Quotes key={chosen} tenantId={chosen} company={list.find((c) => c.id === chosen)?.name ?? 'this company'} />
          ) : (
            <Margin key={chosen} tenantId={chosen} />
          )}
        </>
      )}
    </div>
  );
}

/**
 * What the platform owes its payees, across every company.
 *
 * A list rather than an editor: closing a period, issuing a statement and
 * recording a payment all happen on the payee's own page, where whoever is
 * doing it can see the rates and the history that produced the figure. This
 * is the view that answers "who is waiting to be paid" and links there.
 */
function Payables({ kind }: { kind: 'PARTNER' | 'PHYSICIAN' }) {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const rows = useQuery<{ items: SettlementRow[] }>(
    () => api.get(`/settlements?payeeType=${kind}&limit=200`), [kind],
  );

  const items = (rows.data?.items ?? []).filter((s) => s.status !== 'VOID');
  const issued = items.filter((s) => s.status !== 'DRAFT');
  const outstanding = issued.reduce((n, s) => n + s.outstandingPaise, 0);
  const paid = issued.reduce((n, s) => n + s.paidPaise, 0);
  const unpriced = items.reduce((n, s) => n + s.unpricedCount, 0);
  const noun = kind === 'PARTNER' ? 'venue' : 'physician';
  // A payee's own page is where a period is closed and paid. A venue page is
  // open to anyone who reads partners; a physician's page needs the routing
  // permission, which Finance does not hold — so a Finance login is not
  // offered a link that would only be refused.
  const canOpen = kind === 'PARTNER' ? permissions.has('partner.read') : permissions.has('examination.route');
  const href = (s: SettlementRow) => (kind === 'PARTNER'
    ? `/admin/partners/${s.partnerId}?tab=statements`
    : `/admin/physicians/${s.physicianId}?tab=statements`);

  return (
    <div className="space-y-3">
      <KeyFigures
        label={`${noun} payables`}
        figures={[
          { key: 'out', label: 'Outstanding', value: formatPaise(outstanding), loading: rows.loading,
            tone: outstanding > 0 ? 'dueSoon' : undefined, hint: 'Issued, not yet paid' },
          { key: 'paid', label: 'Paid to date', value: formatPaise(paid), loading: rows.loading, hint: 'Recorded payments' },
          { key: 'n', label: 'Statements', value: String(items.length), loading: rows.loading,
            hint: `${items.length - issued.length} draft` },
          unpriced > 0 && { key: 'unpriced', label: 'Unpriced events', value: String(unpriced), tone: 'dueSoon',
            hint: 'Left out of the totals' },
        ]}
      />
      {unpriced > 0 && (
        <Alert tone="warning">
          {unpriced} settled event{unpriced === 1 ? '' : 's'} had no published rate and {unpriced === 1 ? 'is' : 'are'} not in these
          figures. Publish a rate covering the period on the {noun}’s page, then reissue the statement.
        </Alert>
      )}
      {!canOpen && items.length > 0 && (
        <p className="text-caption text-neutral-500">
          Periods are closed and paid from each {noun}’s page, which your role cannot open. Ask a platform administrator to act on a statement.
        </p>
      )}

      <DataTable<SettlementRow>
        rows={items}
        loading={rows.loading}
        error={rows.error}
        onRetry={rows.reload}
        rowKey={(s) => s.id}
        {...(canOpen ? { onRowClick: (s: SettlementRow) => navigate(href(s)) } : {})}
        empty={{
          title: `Nothing owed to any ${noun} yet`,
          hint: `A statement appears once a period is closed on a ${noun}’s Statements tab for completed work.`,
        }}
        columns={[
          { key: 'payee', header: kind === 'PARTNER' ? 'Venue' : 'Physician', alwaysVisible: true, cell: (s) => {
            const name = s.partner?.name ?? s.physician?.fullName ?? '—';
            return (
              <div>
                {canOpen
                  ? <Link to={href(s)} onClick={(e) => e.stopPropagation()} className="text-sm font-medium text-primary-600 hover:text-primary-700">{name}</Link>
                  : <span className="text-sm font-medium text-neutral-900">{name}</span>}
                <div className="font-mono text-caption text-neutral-500">{s.reference}</div>
              </div>
            );
          } },
          { key: 'period', header: 'Period', cell: (s) => <span className="whitespace-nowrap text-small">{periodLabel(s.periodStart, s.periodEnd)}</span> },
          { key: 'events', header: 'Events', align: 'right', cell: (s) => (
            <span className="tabular-nums text-small text-neutral-700">
              {s.sourceCount}
              {s.unpricedCount > 0 && <span className="block text-caption text-warning-600">+{s.unpricedCount} unpriced</span>}
            </span>
          ) },
          { key: 'amount', header: 'Amount', align: 'right', cell: (s) => (
            <span className="whitespace-nowrap tabular-nums text-sm">{formatPaise(s.subtotalPaise, s.currency)}</span>
          ) },
          { key: 'out', header: 'Outstanding', align: 'right', cell: (s) => (
            <span className={`whitespace-nowrap tabular-nums text-sm ${s.outstandingPaise > 0 ? 'font-semibold text-neutral-900' : 'text-neutral-500'}`}>
              {formatPaise(s.outstandingPaise, s.currency)}
            </span>
          ) },
          { key: 'status', header: 'Status', mobileLabel: false, cell: (s) => (
            <StatusBadge tone={SETTLEMENT_TONE[s.status] ?? 'neutral'}>
              {SETTLEMENT_STATUS_LABEL[s.status] ?? s.status}
            </StatusBadge>
          ) },
        ]}
      />
    </div>
  );
}

const SETTLEMENT_TONE: Record<string, Tone> = {
  DRAFT: 'pending', ISSUED: 'dueSoon', PARTIALLY_PAID: 'dueSoon', PAID: 'covered', VOID: 'neutral',
};

/**
 * A company's invoices, with the four amounts kept apart: billed, confirmed
 * as paid, submitted and awaiting verification, and outstanding. Outstanding
 * is the API's figure and counts confirmed payments only.
 */
function Invoices({ tenantId, pending, picker }: { tenantId: string; pending: PaymentSubmissionQueueRow[] | null; picker?: ReactNode }) {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [show, setShow] = useState<'all' | 'open' | 'pending' | 'paid' | 'draft'>('all');
  const invoices = useQuery<InvoicePage>(
    () => api.get(`/invoices?tenantId=${tenantId}&limit=200`), [tenantId],
  );
  const t = invoices.data?.totals;
  const pendingBy = new Map<string, number>();
  for (const r of pending ?? []) pendingBy.set(r.invoiceId, (pendingBy.get(r.invoiceId) ?? 0) + r.amountPaise);
  const pendingTotal = (pending ?? []).reduce((n, r) => n + r.amountPaise, 0);
  const canGenerate = permissions.has('invoice.create');
  const open = (i: InvoiceRow) => navigate(`/admin/invoices/${i.id}?tenantId=${tenantId}`);
  const all = invoices.data?.items ?? [];
  const owes = (i: InvoiceRow) => i.status === 'ISSUED' && (i.outstandingPaise ?? 0) > 0;
  const matches = (k: typeof show) => (i: InvoiceRow) => k === 'all'
    || (k === 'open' && owes(i))
    || (k === 'pending' && pendingBy.has(i.id))
    || (k === 'paid' && i.status === 'ISSUED' && (i.outstandingPaise ?? 0) <= 0)
    || (k === 'draft' && i.status === 'DRAFT');
  // What needs Finance first: submissions to verify, then money still owed,
  // then drafts not yet sent; settled and void invoices last. Stable within
  // each group, so the server's newest-first order holds.
  const rank = (i: InvoiceRow) => (pendingBy.has(i.id) ? 0 : owes(i) ? 1 : i.status === 'DRAFT' ? 2 : 3);
  const rows = all.filter(matches(show)).map((i, n) => ({ i, n }))
    .sort((a, b) => rank(a.i) - rank(b.i) || a.n - b.n).map((x) => x.i);
  const chips: Array<{ key: typeof show; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'To be paid' },
    ...(pending ? [{ key: 'pending' as const, label: 'Awaiting verification' }] : []),
    { key: 'paid', label: 'Paid' },
    { key: 'draft', label: 'Drafts' },
  ];
  const full = all.length >= 200;

  return (
    <div className="space-y-3">
      <KeyFigures
        label="Company account"
        figures={[
          { key: 'billed', label: 'Billed', value: formatPaise(t?.billedPaise ?? 0), loading: invoices.loading,
            hint: `${t?.issuedCount ?? 0} issued · ${t?.draftCount ?? 0} draft` },
          { key: 'paid', label: 'Paid (confirmed)', value: formatPaise(t?.paidPaise ?? 0), loading: invoices.loading,
            hint: 'Recorded on the ledger' },
          pending && { key: 'pending', label: 'Awaiting verification', value: formatPaise(pendingTotal), loading: invoices.loading,
            tone: pendingTotal > 0 ? 'pending' : undefined,
            hint: pendingTotal > 0 ? 'Not yet confirmed' : 'None submitted',
            ...(pendingTotal > 0 ? { onClick: () => setShow('pending') } : {}) },
          { key: 'out', label: 'Outstanding', value: formatPaise(t?.outstandingPaise ?? 0), loading: invoices.loading,
            tone: (t?.outstandingPaise ?? 0) > 0 ? 'overdue' : undefined,
            hint: `${t?.openCount ?? 0} open invoice${t?.openCount === 1 ? '' : 's'}`,
            ...((t?.openCount ?? 0) > 0 ? { onClick: () => setShow('open') } : {}) },
        ]}
      />

      <div className="flex flex-wrap items-end gap-3">
        {picker}
        {canGenerate && <Button className="sm:ml-auto" onClick={() => setGenerating(true)}>Generate invoice</Button>}
      </div>

      <div role="group" aria-label="Show invoices" className="flex gap-2 max-md:-mx-4 max-md:overflow-x-auto max-md:px-4 max-md:pb-1">
        {chips.map((c) => {
          const on = c.key === show;
          const n = all.filter(matches(c.key)).length;
          return (
            <button
              key={c.key} type="button" aria-pressed={on} onClick={() => setShow(c.key)}
              className={on
                ? 'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-primary-600 px-3.5 text-small font-semibold text-white max-md:h-10'
                : 'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-3.5 text-small font-medium text-neutral-700 hover:bg-neutral-50 max-md:h-10'}
            >
              {c.label}
              {/* A full page is not the whole ledger: counts only when everything is loaded. */}
              {!invoices.loading && !full && <span className={on ? 'tabular-nums text-white/80' : 'tabular-nums text-neutral-500'}>{n}</span>}
            </button>
          );
        })}
      </div>
      {show === 'pending' && pendingTotal > 0 && (
        <p className="text-small text-neutral-700">
          Submitted by the company and not yet checked against the bank statement — not counted as paid.{' '}
          <Link to="/admin/payments" className="font-medium text-primary-600 hover:text-primary-700">Review in Payment review →</Link>
        </p>
      )}
      {full && <p className="text-caption text-neutral-500">Showing the latest 200 invoices; counts are hidden because older invoices are not loaded.</p>}

      <DataTable<InvoiceRow>
        rows={rows}
        loading={invoices.loading}
        error={invoices.error}
        onRetry={invoices.reload}
        rowKey={(i) => i.id}
        onRowClick={open}
        empty={show !== 'all' && all.length > 0 ? {
          title: 'No invoices match',
          hint: 'Nothing in this company’s invoices is in that state. Show all invoices to see the rest.',
          action: <Button variant="secondary" onClick={() => setShow('all')}>Show all invoices</Button>,
        } : {
          title: 'No invoices for this company yet',
          hint: canGenerate
            ? 'Generate an invoice to bill a plant for its certified examinations. It stays a draft until you issue it.'
            : 'Invoices appear here once they are generated.',
          ...(canGenerate ? { action: <Button onClick={() => setGenerating(true)}>Generate invoice</Button> } : {}),
        }}
        columns={[
          { key: 'number', header: 'Invoice', alwaysVisible: true, cell: (i) => (
            <div className="min-w-[9rem]">
              {/* A draft has no number in the tax-invoice series until it is issued. */}
              <Link
                to={`/admin/invoices/${i.id}?tenantId=${tenantId}`} onClick={(e) => e.stopPropagation()}
                className={`whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700 ${isDraftNumber(i.invoiceNumber) ? '' : 'font-mono'}`}
                title={isDraftNumber(i.invoiceNumber) ? `${i.invoiceNumber} — numbered when issued` : undefined}
              >
                {invoiceName(i)}
              </Link>
              <div className="max-w-[14rem] truncate text-caption text-neutral-500" title={i.plant?.name ?? i.billToPlantName}>
                {i.plant?.code ?? i.billToPlantCode} · {i.plant?.name ?? i.billToPlantName}
              </div>
            </div>
          ) },
          { key: 'period', header: 'Period', cell: (i) => (
            <div className="whitespace-nowrap text-small">
              {periodLabel(i.periodStart, i.periodEnd)}
              <div className="text-caption text-neutral-500">{i.lineCount ?? 0} line{i.lineCount === 1 ? '' : 's'}</div>
            </div>
          ) },
          { key: 'total', header: 'Amount', align: 'right', cell: (i) => (
            <div className="whitespace-nowrap">
              <div className="tabular-nums text-sm font-medium text-neutral-900">{formatPaise(i.totalPaise, i.currency)}</div>
              <div className="text-caption text-neutral-500">
                {i.taxTreatment === 'UNDETERMINED'
                  ? 'Tax not decided'
                  : i.taxRateBasisPoints ? `Incl. ${i.taxRateBasisPoints / 100}% GST` : TAX_TREATMENT_LABEL[i.taxTreatment]}
              </div>
            </div>
          ) },
          { key: 'paid', header: 'Paid (confirmed)', align: 'right', hideOnMobile: true, cell: (i) => (
            i.status === 'ISSUED'
              ? <span className="whitespace-nowrap tabular-nums text-sm">{formatPaise(i.paidPaise ?? 0, i.currency)}</span>
              : <span className="text-small text-neutral-500">—</span>
          ) },
          { key: 'out', header: 'Outstanding', align: 'right', cell: (i) => (
            i.status === 'ISSUED'
              ? <span className={`whitespace-nowrap tabular-nums text-sm ${(i.outstandingPaise ?? 0) > 0 ? 'font-medium text-overdue-600' : 'text-neutral-500'}`}>
                  {formatPaise(i.outstandingPaise ?? 0, i.currency)}
                </span>
              : <span className="text-small text-neutral-500">—</span>
          ) },
          { key: 'status', header: 'Status', mobileLabel: false, cell: (i) => <InvoiceState invoice={i} pending={pendingBy.get(i.id)} /> },
        ]}
      />

      {generating && (
        <GenerateDialog
          tenantId={tenantId}
          onClose={() => setGenerating(false)}
          onDone={() => { setGenerating(false); invoices.reload(); }}
        />
      )}
    </div>
  );
}

/**
 * A draft says it has not been sent; an issued invoice says where payment
 * stands, from the confirmed ledger; a submitted payment is its own badge and
 * never turns an invoice "Paid".
 */
function InvoiceState({ invoice: i, pending }: { invoice: InvoiceRow; pending?: number }) {
  if (i.status !== 'ISSUED') {
    return <StatusBadge tone={i.status === 'DRAFT' ? 'pending' : 'neutral'}>{INVOICE_STATUS_LABEL[i.status] ?? i.status}</StatusBadge>;
  }
  const s = standingOf(i);
  return (
    <div className="min-w-[7rem]">
      <StatusBadge tone={STANDING_TONE[s]}>{STANDING_LABEL[s]}</StatusBadge>
      {!!pending && (
        <div className="mt-0.5 text-caption font-medium text-pending-600">
          {formatPaise(pending, i.currency)} submitted, awaiting verification
        </div>
      )}
    </div>
  );
}

function monthBounds(offset = 0) {
  const now = new Date();
  return {
    first: ymd(new Date(now.getFullYear(), now.getMonth() + offset, 1)),
    last: ymd(new Date(now.getFullYear(), now.getMonth() + offset + 1, 0)),
  };
}

function GenerateDialog({ tenantId, onClose, onDone }: {
  tenantId: string; onClose: () => void; onDone: () => void;
}) {
  const { first, last } = monthBounds();
  const [plantId, setPlantId] = useState('');
  const [periodStart, setStart] = useState(first);
  const [periodEnd, setEnd] = useState(last);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per dialog, so a retried submit cannot act twice.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const plantError = !plantId ? 'Choose the plant to bill.' : null;
  const periodError = !periodStart || !periodEnd ? 'Enter both dates.' : periodEnd < periodStart ? 'The end date is before the start date.' : null;

  async function submit() {
    setTried(true);
    if (plantError || periodError) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/invoices/generate?tenantId=${tenantId}`, { plantId, periodStart, periodEnd }, idempotencyKey);
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog
      title="Generate invoice"
      info="Creates a draft invoice for one plant’s unbilled examinations certified in the period, priced on the rate card in force. Nothing is sent to the company until you issue it. Generating again is safe: examinations already billed are skipped."
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Generate draft</Button>
        </>
      )}
    >
      <div className="mt-3 space-y-3">
        <BillablePlantSelect label="Plant" required error={tried ? plantError : null}
          tenantId={tenantId} value={plantId} onChange={setPlantId} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Period start" required>
            {(p) => <Input {...p} type="date" value={periodStart} onChange={(e) => setStart(e.target.value)} />}
          </Field>
          <Field label="Period end" error={tried || periodEnd < periodStart ? periodError : null} required>
            {(p) => <Input {...p} type="date" value={periodEnd} min={periodStart} onChange={(e) => setEnd(e.target.value)} />}
          </Field>
        </div>
      </div>
      {error && <Alert tone="danger" className="mt-3">{error}</Alert>}
    </Dialog>
  );
}

const CARD_STATUS: Record<string, { label: string; tone: Tone }> = {
  DRAFT: { label: 'Draft', tone: 'pending' },
  PUBLISHED: { label: 'Published', tone: 'covered' },
  SUPERSEDED: { label: 'Superseded', tone: 'neutral' },
};

function RateCards({ tenantId, company }: { tenantId: string; company: string }) {
  const { permissions } = useAuth();
  const [showSuperseded, setShow] = useState(false);
  const [creating, setCreating] = useState(false);
  const [publishing, setPublishing] = useState<RateCardRow | null>(null);
  const cards = useQuery<{ items: RateCardRow[] }>(
    () => api.get(`/rate-cards?tenantId=${tenantId}&includeSuperseded=${showSuperseded}`),
    [tenantId, showSuperseded],
  );
  const canCreate = permissions.has('invoice.create');
  const canPublish = permissions.has('invoice.approve');
  const current = (cards.data?.items ?? []).find((c) => c.status === 'PUBLISHED');

  async function publish(card: RateCardRow) {
    await api.post(`/rate-cards/${card.id}/publish?tenantId=${tenantId}`, {});
    setPublishing(null);
    cards.reload();
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Checkbox label="Show superseded cards" checked={showSuperseded} onChange={(e) => setShow(e.target.checked)} />
          {canCreate && <Button size="sm" onClick={() => setCreating(true)}>Create rate card</Button>}
        </div>
      </div>

      <DataTable<RateCardRow>
        rows={cards.data?.items ?? []}
        loading={cards.loading}
        error={cards.error}
        onRetry={cards.reload}
        rowKey={(c) => c.id}
        empty={{
          title: 'No rate card for this company',
          hint: 'Invoices cannot be priced until a rate card is published. Create one, check it, then publish it.',
          ...(canCreate ? { action: <Button onClick={() => setCreating(true)}>Create rate card</Button> } : {}),
        }}
        columns={[
          { key: 'name', header: 'Rate card', alwaysVisible: true, cell: (c) => (
            <div>
              <div className="text-sm font-medium text-neutral-900">{c.name}</div>
              <div className="text-caption text-neutral-500">
                From {formatDate(c.effectiveFrom)}{c.effectiveTo ? ` to ${formatDate(c.effectiveTo)}` : c.status === 'PUBLISHED' ? ' · in force' : ''}
              </div>
            </div>
          ) },
          { key: 'lines', header: 'Prices', cell: (c) => (
            <ul className="min-w-[14rem] space-y-0.5">
              {c.lines.map((l) => (
                <li key={l.id} className="flex justify-between gap-3 text-small text-neutral-700">
                  <span>
                    {l.protocol?.name
                      ?? (l.package ? `${l.package.name} (package)` : null)
                      ?? (l.test ? `Extra test ${l.test.code}` : null)
                      ?? (l.profile ? `${l.profile.name} (profile)` : null)
                      ?? (l.examinationType
                        ? (EXAM_TYPE_LABEL[l.examinationType] ?? l.examinationType)
                        : 'Any other examination')}
                  </span>
                  <span className="shrink-0 tabular-nums text-neutral-900">{formatPaise(l.amountPaise, c.currency)}</span>
                </li>
              ))}
            </ul>
          ) },
          { key: 'status', header: 'Status', mobileLabel: false, cell: (c) => (
            <StatusBadge tone={CARD_STATUS[c.status]?.tone ?? 'neutral'}>{CARD_STATUS[c.status]?.label ?? c.status}</StatusBadge>
          ) },
          ...(canPublish ? [{ key: 'act', header: 'Actions', align: 'right' as const, alwaysVisible: true, mobileLabel: '', cell: (c: RateCardRow) => (
            c.status === 'DRAFT'
              ? (
                <RowActions>
                  <IconAction label={`Publish rate card ${c.name}`} icon={BadgeCheck} primary onClick={() => setPublishing(c)} />
                </RowActions>
              )
              : null
          ) }] : []),
        ]}
      />

      {creating && (
        <NewRateCardDialog
          tenantId={tenantId}
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); cards.reload(); }}
        />
      )}
      {publishing && (
        <ConfirmDialog
          title={`Publish “${publishing.name}”?`}
          what={`From ${formatDate(publishing.effectiveFrom)}, every invoice generated for ${company} is priced on this card.`}
          affects={current
            ? `“${current.name}” stops applying from that date. Invoices already generated keep the prices they were billed at.`
            : 'This becomes the company’s first published rate card, so invoices can be generated from that date.'}
          irreversible="A published rate card cannot be edited or withdrawn. To change a price, create and publish a new card."
          confirmLabel="Publish rate card"
          onConfirm={() => publish(publishing)}
          onCancel={() => setPublishing(null)}
        />
      )}
    </div>
  );
}

const EXAMINATION_TYPES = [
  'PRE_EMPLOYMENT', 'PERIODIC_OHC', 'ANNUAL_HEALTH_CHECKUP', 'SPECIAL',
  'RETURN_TO_WORK', 'POST_INCIDENT', 'EXIT', 'RETEST',
] as const;

type DraftLine = { basis: 'protocol' | 'package' | 'type' | 'test' | 'profile' | 'fallback'; ref: string; rupees: string };

/**
 * Charged minus paid, for the chosen company, per period. Both sides are
 * frozen lines already, so this is a join the server does and nothing that
 * could drift from the invoices and statements it reads.
 */
function Margin({ tenantId }: { tenantId: string }) {
  const [from, setFrom] = useState(monthBounds().first);
  const [to, setTo] = useState(localToday());
  const bad = !from || !to || to < from;
  const report = useQuery<MarginReport | null>(
    () => (bad ? Promise.resolve(null) : api.get(`/billing/margin?periodStart=${from}&periodEnd=${to}&tenantId=${tenantId}`)),
    [tenantId, from, to, bad],
  );
  const r = report.data;
  // A margin is invoiced less paid out, and "paid out" only knows the costs a
  // settlement has claimed. While any venue or physician event is unsettled
  // the figure is the invoice wearing a margin's name — ₹1,200 "gross margin"
  // at 100% on an examination whose costs nobody had priced yet (audit of
  // 1 Oct 2026). So it is headed as provisional until nothing is outstanding.
  const unsettled = r?.totals.unsettledEvents ?? 0;
  const provisional = unsettled > 0;
  const events = (n: number) => `${n} payout event${n === 1 ? '' : 's'}`;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <Field label="From">
          {(p) => <Input {...p} type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-44" />}
        </Field>
        <Field label="To" error={bad && from && to ? 'The end date is before the start date.' : null}>
          {(p) => <Input {...p} type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="w-44" />}
        </Field>
      </div>
      {report.error && <ErrorState detail={report.error} onRetry={report.reload} />}
      <KeyFigures
        label="Margin for the period"
        figures={[
          { key: 'n', label: 'Examinations billed', value: String(r?.totals.orders ?? 0), loading: report.loading, hint: 'Invoice lines in the period' },
          { key: 'billed', label: 'Invoiced', value: formatPaise(r?.totals.billedPaise ?? 0), loading: report.loading, hint: 'To the company' },
          { key: 'cost', label: provisional ? 'Paid out so far' : 'Paid out', value: formatPaise(r?.totals.payoutPaise ?? 0), loading: report.loading,
            tone: provisional ? 'pending' : undefined,
            hint: provisional ? `${events(unsettled)} not settled yet` : 'Venues and physicians' },
          { key: 'margin', label: provisional ? 'Provisional margin' : 'Gross margin', value: formatPaise(r?.totals.marginPaise ?? 0), loading: report.loading,
            tone: (r?.totals.marginPaise ?? 0) < 0 ? 'overdue' : provisional ? 'pending' : undefined,
            hint: provisional ? 'Before unsettled payouts — not the real margin' : 'Invoiced less paid out' },
        ]}
      />
      {provisional && (
        <Alert tone="warning" title="This is not the margin yet">
          {events(unsettled)} for these examinations {unsettled === 1 ? 'has' : 'have'} not been settled — a completed visit, a released result
          or a signed review that no venue or physician statement has claimed. Their cost is not known here and is counted as ₹0,
          so the margin shown is the amount invoiced less only what has been settled so far. It will fall as periods are closed
          under Lab payables and Physician payables.
        </Alert>
      )}
      <DataTable<MarginReport['orders'][number]>
        rows={r?.orders ?? []}
        loading={report.loading}
        rowKey={(o) => o.id}
        empty={{ title: 'No billed examinations in this period', hint: 'Margin is worked out from issued invoices. Generate and issue an invoice for the period, or widen the dates.' }}
        columns={[
          { key: 'who', header: 'Examination', alwaysVisible: true, cell: (o) => (
            <div>
              <div className="text-small text-neutral-900">{EXAM_TYPE_LABEL[o.examinationType] ?? o.examinationType}</div>
              <div className="font-mono text-caption text-neutral-500">{o.employeeCode}</div>
            </div>
          ) },
          { key: 'inv', header: 'Invoice', cell: (o) => (
            <span className="whitespace-nowrap font-mono text-small text-neutral-700">
              {o.invoiceNumber}
              {o.invoiceStatus === 'DRAFT' && <span className="block font-sans text-caption text-pending-600">Draft — not issued</span>}
            </span>
          ) },
          { key: 'billed', header: 'Invoiced', align: 'right', cell: (o) => <span className="whitespace-nowrap tabular-nums text-sm">{formatPaise(o.billedPaise)}</span> },
          { key: 'paid', header: 'Paid out', align: 'right', cell: (o) => (
            <span className="whitespace-nowrap tabular-nums text-sm">
              {formatPaise(o.payoutPaise)}
              {o.unsettled ? <span className="block text-caption text-warning-600">+{o.unsettled} not settled</span> : null}
            </span>
          ) },
          { key: 'margin', header: 'Margin', align: 'right', cell: (o) => (
            <span className={`whitespace-nowrap tabular-nums text-sm ${o.unsettled ? 'font-normal text-neutral-600' : 'font-semibold'} ${o.marginPaise < 0 ? 'text-danger-600' : o.unsettled ? '' : 'text-neutral-900'}`}>
              {formatPaise(o.marginPaise)}
              {o.unsettled ? <span className="block text-caption text-warning-600">provisional</span> : null}
            </span>
          ) },
        ]}
      />
      {r && r.byPayee.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-h4 text-neutral-900">Paid out, by venue and physician</h2>
          <DataTable<MarginReport['byPayee'][number]>
            rows={r.byPayee}
            rowKey={(p) => `${p.payeeType}:${p.payeeId}`}
            dense
            columns={[
              { key: 'name', header: 'Payee', alwaysVisible: true, cell: (p) => (
                <div>
                  <div className="text-small text-neutral-900">{p.name}</div>
                  <div className="text-caption text-neutral-500">{p.payeeType === 'PARTNER' ? 'Venue' : 'Physician'}</div>
                </div>
              ) },
              { key: 'events', header: 'Events', align: 'right', cell: (p) => <span className="tabular-nums">{p.events}</span> },
              { key: 'orders', header: 'Examinations', align: 'right', cell: (p) => <span className="tabular-nums">{p.orders}</span> },
              { key: 'amt', header: 'Paid out', align: 'right', cell: (p) => <span className="whitespace-nowrap tabular-nums font-medium">{formatPaise(p.payoutPaise)}</span> },
            ]}
          />
        </section>
      )}
    </div>
  );
}

const typeLabel = (t: string) => EXAM_TYPE_LABEL[t] ?? t.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());

/**
 * Drafting a company's price list.
 *
 * Each line prices one thing, and the most specific line wins when an
 * examination is invoiced: a protocol first, then the examination type, then
 * the fallback. So a card is usually one fallback price plus the exceptions a
 * company negotiated — which is why the form opens with a fallback line
 * rather than an empty table. It is saved as a DRAFT; publishing is the
 * separate, permanent act on the list.
 */
function NewRateCardDialog({ tenantId, onClose, onDone }: {
  tenantId: string; onClose: () => void; onDone: () => void;
}) {
  // The catalogue is read under its own permissions. A role without them
  // (Finance) is offered the prices it can actually set — the default and
  // per examination type — rather than empty pickers fed by refused requests.
  const { permissions } = useAuth();
  const canProtocols = permissions.has('protocol.read');
  const canPackages = permissions.has('package.read');
  const canTests = permissions.has('test.read');
  const none = { items: [] as never[], nextCursor: null };
  const protocols = useQuery<CursorPage<CatalogueProtocol>>(() => (canProtocols ? api.get('/protocols') : Promise.resolve(none)), [canProtocols]);
  const packages = useQuery<{ items: Array<{ id: string; name: string }> } | Array<{ id: string; name: string }>>(() => (canPackages ? api.get('/packages') : Promise.resolve({ items: [] })), [canPackages]);
  const packageList = Array.isArray(packages.data) ? packages.data : (packages.data?.items ?? []);
  const tests = useQuery<CursorPage<{ id: string; code: string; name: string }>>(() => (canTests ? api.get('/tests?brief=true&limit=200') : Promise.resolve(none)), [canTests]);
  // Billing profiles are read under the rate-card permission this dialog
  // already needs, so Finance can price them.
  const billingProfiles = useQuery<{ items: BillingProfileRow[] }>(() => api.get('/billing-profiles'), []);
  const profileList = billingProfiles.data?.items ?? [];
  const [name, setName] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(localToday());
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{ basis: 'fallback', ref: '', rupees: '' }]);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per dialog, so a retried submit cannot act twice.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const protocolName = (id: string) => protocols.data?.items.find((p) => p.id === id)?.name ?? 'Protocol';
  const set = (i: number, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const packageName = (id: string) => packageList.find((p) => p.id === id)?.name ?? 'Package';
  const testName = (id: string) => { const t = tests.data?.items.find((x) => x.id === id); return t ? `Extra test ${t.code}` : 'Extra test'; };
  const payload = lines.map((l) => ({
    label: l.basis === 'protocol' ? protocolName(l.ref)
      : l.basis === 'package' ? packageName(l.ref)
      : l.basis === 'test' ? testName(l.ref)
      : l.basis === 'profile' ? `${profileList.find((p) => p.id === l.ref)?.name ?? 'Billing profile'} (profile)`
      : l.basis === 'type' ? typeLabel(l.ref) : 'Any other examination',
    amountPaise: Math.round(Number(l.rupees) * 100),
    ...(l.basis === 'protocol' ? { protocolId: l.ref } : {}),
    ...(l.basis === 'package' ? { packageId: l.ref } : {}),
    ...(l.basis === 'test' ? { testId: l.ref } : {}),
    ...(l.basis === 'profile' ? { profileId: l.ref } : {}),
    ...(l.basis === 'type' ? { examinationType: l.ref } : {}),
  }));
  const lineProblem = (l: DraftLine) => (l.basis !== 'fallback' && !l.ref)
    ? 'Choose what this price is for.'
    : !(Number(l.rupees) > 0) ? 'Enter a price above ₹0.' : null;
  const keys = lines.map((l) => `${l.basis}:${l.ref}`);
  const duplicate = keys.length !== new Set(keys).size;
  const nameError = name.trim().length < 2 ? 'Give the card a name (at least 2 characters).' : null;
  const valid = !nameError && lines.length > 0 && !lines.some(lineProblem) && !duplicate;

  async function submit() {
    setTried(true);
    if (!valid) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/rate-cards?tenantId=${tenantId}`, {
        name: name.trim(), effectiveFrom, notes: notes.trim() || undefined, lines: payload,
      }, idempotencyKey);
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog
      title="Create rate card"
      onClose={onClose}
      wide
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Save as draft</Button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-small text-neutral-600">
          Saved as a draft — nothing is charged until you publish it.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Rate card name" error={tried ? nameError : null} required>
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. FY 2026–27 annual agreement" autoFocus />}
          </Field>
          <Field label="Effective from" required hint="Examinations on or after this date use these prices.">
            {(p) => <Input {...p} type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />}
          </Field>
        </div>

        <fieldset className="space-y-2">
          <legend className="flex items-center gap-1 text-small font-medium text-neutral-800">
            Prices
            <InfoTip label="About prices">
              When an examination is invoiced, the most specific price wins: a protocol or package first, then the
              examination type, then the default price.
            </InfoTip>
          </legend>
          {lines.map((l, i) => {
            const problem = tried ? lineProblem(l) : null;
            return (
              <div key={i} className="rounded-input border border-neutral-200 p-2">
                <div className="grid gap-2 sm:grid-cols-[11rem_1fr_8rem_auto] sm:items-center">
                  <Select
                    aria-label={`Price ${i + 1}: priced by`}
                    value={l.basis}
                    onChange={(e) => set(i, { basis: e.target.value as DraftLine['basis'], ref: '' })}
                  >
                    <option value="fallback">Default price</option>
                    <option value="type">Examination type</option>
                    {canProtocols && <option value="protocol">Protocol</option>}
                    {canPackages && <option value="package">Checkup package</option>}
                    {canTests && <option value="test">Additional test</option>}
                    <option value="profile">Billing profile (selling price)</option>
                  </Select>
                  {l.basis === 'fallback' ? (
                    <span className="text-caption text-neutral-500">Any examination no other price covers.</span>
                  ) : l.basis === 'package' ? (
                    <SearchSelect aria-label={`Price ${i + 1}: package`} value={l.ref} onChange={(v) => set(i, { ref: v })}
                      placeholder={packages.loading ? 'Loading packages…' : 'Choose a package…'} searchPlaceholder="Search packages…"
                      options={packageList.map((p) => ({ value: p.id, label: p.name }))} />
                  ) : l.basis === 'test' ? (
                    <SearchSelect aria-label={`Price ${i + 1}: test`} value={l.ref} onChange={(v) => set(i, { ref: v })}
                      placeholder={tests.loading ? 'Loading tests…' : 'Choose a test…'} searchPlaceholder="Search tests by name or code…"
                      options={(tests.data?.items ?? []).map((t) => ({ value: t.id, label: t.name, hint: t.code }))} />
                  ) : l.basis === 'profile' ? (
                    <SearchSelect aria-label={`Price ${i + 1}: billing profile`} value={l.ref} onChange={(v) => set(i, { ref: v })}
                      placeholder={billingProfiles.loading ? 'Loading profiles…' : 'Choose a billing profile…'} searchPlaceholder="Search profiles…"
                      options={profileList.map((p) => ({ value: p.id, label: p.name, hint: `${p.code} · ${p.tests.length} tests` }))} />
                  ) : l.basis === 'type' ? (
                    <Select aria-label={`Price ${i + 1}: examination type`} value={l.ref} onChange={(e) => set(i, { ref: e.target.value })}>
                      <option value="">Choose examination type…</option>
                      {EXAMINATION_TYPES.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
                    </Select>
                  ) : (
                    <SearchSelect aria-label={`Price ${i + 1}: protocol`} value={l.ref} onChange={(v) => set(i, { ref: v })}
                      placeholder={protocols.loading ? 'Loading protocols…' : 'Choose a protocol…'} searchPlaceholder="Search protocols by name or code…"
                      options={(protocols.data?.items ?? []).map((p) => ({ value: p.id, label: p.name, hint: p.code }))} />
                  )}
                  <Input
                    aria-label={`Price ${i + 1}: amount in rupees`}
                    inputMode="decimal"
                    placeholder="₹ 0.00"
                    className="text-right tabular-nums"
                    value={l.rupees}
                    invalid={!!problem && !(Number(l.rupees) > 0)}
                    onChange={(e) => set(i, { rupees: e.target.value.replace(/[^0-9.]/g, '') })}
                  />
                  <Button
                    variant="ghost" size="sm"
                    disabled={lines.length === 1}
                    aria-label={`Remove price ${i + 1}`}
                    onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                  >
                    Remove
                  </Button>
                </div>
                {problem && <p className="mt-1 text-caption text-danger-600">{problem}</p>}
              </div>
            );
          })}
          <Button
            variant="secondary" size="sm"
            onClick={() => setLines((ls) => [...ls, { basis: 'type', ref: '', rupees: '' }])}
          >
            + Add a price
          </Button>
          {duplicate && (
            <p className="text-caption text-danger-600">Two prices are for the same item. Remove one of them.</p>
          )}
          {!(canProtocols && canPackages && canTests) && (
            <p className="text-caption text-neutral-500">
              Prices for a specific protocol, package or test need catalogue access your role does not have. Ask a platform administrator to add them.
            </p>
          )}
        </fieldset>

        <Field label="Notes (optional)">
          {(p) => <Input {...p} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Contract reference or discount agreement" />}
        </Field>

        {error && <Alert tone="danger">{error}</Alert>}
      </div>
    </Dialog>
  );
}

interface QuoteRow {
  id: string;
  quoteNumber: string;
  status: string;
  validFrom: string;
  validTo?: string | null;
  grossPaise: number;
  discountPaise: number;
  netPaise: number;
  estimatedMarginBasisPoints?: number | null;
  items?: Array<{
    id: string;
    description: string;
    quantity: number;
    unitPaise: number;
    amountPaise: number;
  }>;
  approvals?: Array<{
    id: string;
    ruleNameSnapshot: string;
    decision: 'PENDING' | 'APPROVED' | 'REJECTED';
    requiredPermission: string;
  }>;
}

const QUOTE_STATUS: Record<string, { label: string; tone: Tone }> = {
  DRAFT: { label: 'Draft', tone: 'pending' },
  PENDING_APPROVAL: { label: 'Needs approval', tone: 'warning' },
  APPROVED: { label: 'Approved', tone: 'info' },
  SENT: { label: 'Sent to company', tone: 'info' },
  ACCEPTED: { label: 'Accepted', tone: 'covered' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
};

type QuoteAct =
  | { kind: 'send'; quote: QuoteRow }
  | { kind: 'accept'; quote: QuoteRow }
  | { kind: 'decide'; quote: QuoteRow; approvalId: string; rule: string; decision: 'APPROVED' | 'REJECTED' };

function Quotes({ tenantId, company }: { tenantId: string; company: string }) {
  const { permissions } = useAuth();
  const [acting, setActing] = useState<QuoteAct | null>(null);
  const quotes = useQuery<QuoteRow[]>(
    () => api.get<QuoteRow[]>(`/quotes?tenantId=${tenantId}`), [tenantId],
  );
  const rows = Array.isArray(quotes.data) ? quotes.data : [];
  // Each act is offered only to a role the API would let do it. An approval
  // names the exact permission it needs — a discount and a thin margin may
  // need different people.
  const canSend = permissions.has('quote.send');
  const canAccept = permissions.has('quote.create');

  async function run(act: QuoteAct) {
    if (act.kind === 'send') {
      await api.post(`/quotes/${act.quote.id}/send?tenantId=${tenantId}`, {}, crypto.randomUUID());
    } else if (act.kind === 'accept') {
      await api.post(`/quotes/${act.quote.id}/accept?tenantId=${tenantId}`, { publishRateCard: true, activateTenant: true }, crypto.randomUUID());
    } else {
      await api.post(`/quotes/approvals/${act.approvalId}/decide?tenantId=${tenantId}`, { decision: act.decision }, crypto.randomUUID());
    }
    setActing(null);
    quotes.reload();
  }

  return (
    <div className="space-y-3">
      <DataTable<QuoteRow>
        rows={rows}
        loading={quotes.loading}
        error={quotes.error}
        onRetry={quotes.reload}
        rowKey={(q) => q.id}
        empty={{
          title: 'No quotes for this company',
          hint: 'A quote is prepared when a company is priced during onboarding. It shows here for approval, sending and acceptance.',
        }}
        columns={[
          { key: 'number', header: 'Quote', alwaysVisible: true, cell: (q) => (
            <div>
              <div className="whitespace-nowrap font-mono text-sm font-medium text-neutral-900">{q.quoteNumber}</div>
              <div className="text-caption text-neutral-500">
                Valid from {formatDate(q.validFrom)}{q.validTo ? ` to ${formatDate(q.validTo)}` : ''} · {q.items?.length ?? 0} item{q.items?.length === 1 ? '' : 's'}
              </div>
            </div>
          ) },
          { key: 'gross', header: 'Gross', align: 'right', hideOnMobile: true, cell: (q) => (
            <span className="whitespace-nowrap tabular-nums text-sm text-neutral-600">{formatPaise(q.grossPaise)}</span>
          ) },
          { key: 'discount', header: 'Discount', align: 'right', cell: (q) => (
            <span className="whitespace-nowrap tabular-nums text-sm text-neutral-600">{q.discountPaise > 0 ? `−${formatPaise(q.discountPaise)}` : '—'}</span>
          ) },
          { key: 'net', header: 'Net', align: 'right', cell: (q) => (
            <span className="whitespace-nowrap tabular-nums text-sm font-semibold text-neutral-900">{formatPaise(q.netPaise)}</span>
          ) },
          { key: 'margin', header: 'Est. margin', align: 'right', cell: (q) => (
            <span className="tabular-nums text-sm text-neutral-700">
              {q.estimatedMarginBasisPoints != null ? `${(q.estimatedMarginBasisPoints / 100).toFixed(1)}%` : '—'}
            </span>
          ) },
          { key: 'status', header: 'Status', mobileLabel: false, cell: (q) => {
            const st = QUOTE_STATUS[q.status] ?? { label: q.status.replace(/_/g, ' ').toLowerCase(), tone: 'neutral' as Tone };
            const waiting = (q.approvals ?? []).filter((a) => a.decision === 'PENDING');
            return (
              <div className="space-y-1">
                <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                {waiting.length > 0 && (
                  <div className="max-w-[14rem] text-caption text-warning-600">Waiting on: {waiting.map((a) => a.ruleNameSnapshot).join(', ')}</div>
                )}
              </div>
            );
          } },
          { key: 'actions', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '', cell: (q) => {
            const waiting = q.status === 'PENDING_APPROVAL'
              ? (q.approvals ?? []).filter((a) => a.decision === 'PENDING' && permissions.has(a.requiredPermission))
              : [];
            return (
              <RowActions className="flex-wrap">
                {waiting.map((a) => (
                  <div key={a.id} className="flex gap-0.5 [@media(pointer:coarse)]:gap-2">
                    <IconAction label={`Reject ${q.quoteNumber} (${a.ruleNameSnapshot})`} icon={X} danger
                      onClick={() => setActing({ kind: 'decide', quote: q, approvalId: a.id, rule: a.ruleNameSnapshot, decision: 'REJECTED' })} />
                    <IconAction label={`Approve ${q.quoteNumber} (${a.ruleNameSnapshot})`} icon={Check}
                      onClick={() => setActing({ kind: 'decide', quote: q, approvalId: a.id, rule: a.ruleNameSnapshot, decision: 'APPROVED' })} />
                  </div>
                ))}
                {canSend && ['DRAFT', 'APPROVED'].includes(q.status) && (
                  <IconAction label={`Mark ${q.quoteNumber} as sent`} icon={Send} onClick={() => setActing({ kind: 'send', quote: q })} />
                )}
                {canAccept && ['SENT', 'APPROVED'].includes(q.status) && (
                  <IconAction label={`Record that ${q.quoteNumber} was accepted`} icon={CheckCircle2} primary onClick={() => setActing({ kind: 'accept', quote: q })} />
                )}
              </RowActions>
            );
          } },
        ]}
      />

      {acting?.kind === 'send' && (
        <ConfirmDialog
          title={`Mark ${acting.quote.quoteNumber} as sent?`}
          what={`The quote is recorded as sent to ${company}, at ${formatPaise(acting.quote.netPaise)} net. Nothing is emailed from here — send the document to the company yourself.`}
          irreversible="A sent quote cannot go back to draft. It can then be accepted when the company agrees."
          confirmLabel="Mark as sent"
          onConfirm={() => run(acting)}
          onCancel={() => setActing(null)}
        />
      )}
      {acting?.kind === 'accept' && (
        <ConfirmDialog
          title={`Accept ${acting.quote.quoteNumber}?`}
          what={`${company} has agreed to this quote. Its prices are published as the company’s rate card “Agreement: ${acting.quote.quoteNumber}”, and the company is activated if it is not active yet.`}
          affects={`${acting.quote.items?.length ?? 0} price line${acting.quote.items?.length === 1 ? '' : 's'} · ${formatPaise(acting.quote.netPaise)} net. From ${formatDate(acting.quote.validFrom)}, invoices are priced on the new card and the current card stops applying.`}
          irreversible="Accepting cannot be undone. The published rate card is permanent; a later price change needs a new card."
          confirmLabel="Accept and publish rate card"
          onConfirm={() => run(acting)}
          onCancel={() => setActing(null)}
        />
      )}
      {acting?.kind === 'decide' && (
        <ConfirmDialog
          title={`${acting.decision === 'APPROVED' ? 'Approve' : 'Reject'} “${acting.rule}”?`}
          what={acting.decision === 'APPROVED'
            ? `You approve ${acting.quote.quoteNumber} under this rule. Once every rule is approved the quote can be sent.`
            : `You reject ${acting.quote.quoteNumber} under this rule. The quote cannot be sent or accepted as it stands.`}
          affects={`${formatPaise(acting.quote.netPaise)} net${acting.quote.discountPaise ? ` after a ${formatPaise(acting.quote.discountPaise)} discount` : ''}${acting.quote.estimatedMarginBasisPoints != null ? ` · estimated margin ${(acting.quote.estimatedMarginBasisPoints / 100).toFixed(1)}%` : ''}.`}
          irreversible="The decision is recorded against your name and cannot be changed."
          tone={acting.decision === 'APPROVED' ? 'success' : 'danger'}
          confirmLabel={acting.decision === 'APPROVED' ? 'Approve' : 'Reject'}
          onConfirm={() => run(acting)}
          onCancel={() => setActing(null)}
        />
      )}
    </div>
  );
}
